import { useMemo, useRef, useState } from 'react';

import {
  CalendarIcon,
  MessageSquareIcon,
  PlayIcon,
  PlusIcon,
  TagIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { DataTable } from '@/components/ui/table/data-table';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import {
  SelectionActionButton,
  SelectionActions,
  TableToolbarRow,
} from '@/components/ui/table/table-toolbar';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import {
  categoryToFilterValue,
  scheduleFrequencyToFilterValue,
  useSimulationsFilters,
} from '@/modules/simulations/hooks/useSimulationsFilters';
import {
  CADENCE_LABEL_KEYS,
  CHANNEL_LABEL_KEYS,
  SCHEDULE_CADENCES,
  SIMULATION_CHANNEL_TYPES,
  type Simulation,
  type SimulationSchedule,
} from '@/modules/simulations/types';
import { listCategories } from '@/modules/simulations/utils';
import { selectAnyFormDirty } from '@/redux/forms/selectors';
import { setSidebarContent, setSidebarMeta } from '@/redux/sidebar/actions';
import { selectOpenSidebar, selectSidebarMeta } from '@/redux/sidebar/selectors';

import { columns } from './columns';

interface SimulationsTableProps {
  simulations: Simulation[];
  schedules: SimulationSchedule[] | undefined;
  isLoading: boolean;
  onRun: (ids: string[]) => void;
  onRunAll: () => void;
  onDelete: (id: string) => void;
  onBulkDelete: (ids: string[]) => Promise<void>;
}

export const SimulationsTable = ({
  simulations,
  schedules,
  isLoading,
  onRun,
  onRunAll,
  onDelete,
  onBulkDelete,
}: SimulationsTableProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sidebarContent = useSelector(selectOpenSidebar);
  const sidebarMeta = useSelector(selectSidebarMeta);
  const anyFormDirty = useSelector(selectAnyFormDirty);
  const activeRowId =
    sidebarContent === 'edit_simulation' && typeof sidebarMeta?.simulationId === 'string'
      ? sidebarMeta.simulationId
      : undefined;
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  const [pendingRunAll, setPendingRunAll] = useState(false);
  const [search, setSearch] = useState('');
  const [openPillId, setOpenPillId] = useState<string | null>(null);

  const { filters, activeFilterCount, setFilter, removeFilter, resetFilters, filteredSimulations } =
    useSimulationsFilters(simulations);

  const categories = useMemo(() => listCategories(simulations), [simulations]);

  const validSimulationIds = useMemo(
    () => new Set(simulations.map((s) => s.simulation_id)),
    [simulations]
  );

  const filterDefinitions = useMemo((): FilterDefinition[] => {
    // Always include the Uncategorized sentinel so persisted filter values resolve to a
    // label even if the data no longer has any null-category rows (the prune effect in
    // useSimulationsFilters cleans stale values on the next tick, but this keeps the
    // badge text stable through that window).
    const uncategorizedSentinel = categoryToFilterValue(null);
    const categoryOptions = [
      ...categories
        .filter((c): c is string => c !== null)
        .map((c) => ({ label: c, value: categoryToFilterValue(c) })),
      { label: t('simulations.category_uncategorized'), value: uncategorizedSentinel },
    ];

    const channelOptions = SIMULATION_CHANNEL_TYPES.map((ch) => ({
      label: t(CHANNEL_LABEL_KEYS[ch]),
      value: ch,
    }));

    const scheduleOptions = [
      ...SCHEDULE_CADENCES.map((c) => ({
        label: t(CADENCE_LABEL_KEYS[c]),
        value: scheduleFrequencyToFilterValue(c),
      })),
      {
        label: t('simulations.schedule_cadence_none'),
        value: scheduleFrequencyToFilterValue(null),
      },
    ];

    return [
      {
        id: 'category',
        label: t('simulations.filter_category'),
        icon: TagIcon,
        type: 'checkbox',
        options: categoryOptions,
      },
      {
        id: 'channel_type',
        label: t('simulations.filter_channel'),
        icon: MessageSquareIcon,
        type: 'checkbox',
        options: channelOptions,
      },
      {
        id: 'schedule_frequency',
        label: t('simulations.filter_schedule'),
        icon: CalendarIcon,
        type: 'checkbox',
        options: scheduleOptions,
      },
    ];
  }, [categories, t]);

  const filterBadges = useMemo(
    () => buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter }),
    [filters, filterDefinitions, removeFilter, setFilter]
  );

  const displayedSimulations = useMemo(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) {
      return filteredSimulations;
    }
    return filteredSimulations.filter((s) => s.name.toLowerCase().includes(lower));
  }, [filteredSimulations, search]);

  const openEditPanel = (id: string) => {
    dispatch(setSidebarContent('edit_simulation'));
    dispatch(setSidebarMeta({ simulationId: id }));
  };

  const handleEdit = (id: string) => {
    // Switching from the create panel to edit would unmount the create panel and
    // silently discard any in-progress form data, since the panel's internal dirty
    // guard can't intercept its own unmount. Prompt first when that applies.
    if (sidebarContent === 'create_simulation' && anyFormDirty) {
      setPendingEditId(id);
      return;
    }
    openEditPanel(id);
  };

  const tableColumns = useMemo(
    () => columns({ onRun, onDelete, schedules }),
    [onRun, onDelete, schedules]
  );

  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);
  const {
    columnVisibility,
    hideableColumns,
    setColumnVisibility,
    resetColumnVisibility,
    hasHiddenColumns,
  } = useColumnVisibility(tableColumns, 'simulationsColumnVisibility');

  const handleConfirmDelete = async () => {
    const ids = pendingDeleteIds;
    setPendingDeleteIds([]);
    try {
      await onBulkDelete(ids);
    } catch {
      // Error toast is handled by the mutation's onError callback
    }
  };

  return (
    <DataTable.Root
      fill
      className="min-h-0 flex-1"
      columns={tableColumns}
      data={displayedSimulations}
      rowCount={displayedSimulations.length}
      resourceName="simulation"
      emptyStateMessage={t('simulations.no_simulations_found')}
      isLoading={isLoading}
      getRowId={(row: Simulation) => row.simulation_id}
      enableRowHover
      onRowClick={(row) => handleEdit(row.simulation_id)}
      activeRowId={activeRowId}
      autoResetPageIndex
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
    >
      <DataTable.Toolbar<Simulation>>
        {(table) => {
          // Read selection from state, not the filtered row model — search is applied to
          // `data` before TanStack sees it, so filtered-out rows aren't in any row model.
          const rowSelection = table.getState().rowSelection;
          const selectedIds = Object.keys(rowSelection).filter(
            (id) => rowSelection[id] && validSimulationIds.has(id)
          );
          const selectedCount = selectedIds.length;

          return (
            <div className="mb-4 flex flex-col gap-4">
              <TableToolbarRow
                start={
                  <SearchBar
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('simulations.search_simulations')}
                  />
                }
                center={
                  <SelectionActions
                    count={selectedCount}
                    getLabel={(count) => t('simulations.selected', { count })}
                    onDeselectAll={() => table.resetRowSelection()}
                  >
                    <SelectionActionButton
                      label={t('simulations.run')}
                      onClick={() => onRun(selectedIds)}
                    >
                      <PlayIcon />
                    </SelectionActionButton>
                    <SelectionActionButton
                      label={t('common.delete')}
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      onClick={() => setPendingDeleteIds(selectedIds)}
                    >
                      <Trash2Icon />
                    </SelectionActionButton>
                  </SelectionActions>
                }
                end={
                  <>
                    <ComplexFilterCombobox
                      filterDefinitions={filterDefinitions}
                      filters={filters}
                      setFilter={setFilter}
                      removeFilter={removeFilter}
                      activeFilterCount={activeFilterCount}
                      onOpenActivePill={setOpenPillId}
                    />
                    <ColumnVisibilityMenu
                      columns={hideableColumns}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={setColumnVisibility}
                      onReset={resetColumnVisibility}
                      open={isCustomizeColumnsOpen}
                      onOpenChange={setIsCustomizeColumnsOpen}
                      onInteractOutside={(e) => {
                        if (e.target === customizeButtonRef.current) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    />
                    {simulations.length > 0 && (
                      <Button
                        variant="outline-neutral"
                        size="sm"
                        onClick={() => setPendingRunAll(true)}
                      >
                        <PlayIcon size={16} />
                        {t('simulations.run_all')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => dispatch(setSidebarContent('create_simulation'))}
                    >
                      <PlusIcon size={16} />
                      {t('common.create')}
                    </Button>
                  </>
                }
              />
              <ComplexFilterBar
                filters={filterBadges}
                openPillId={openPillId}
                onOpenPillChange={setOpenPillId}
                onClearAll={resetFilters}
              />
            </div>
          );
        }}
      </DataTable.Toolbar>
      <DataTable.Content />
      <DataTable.Pagination
        centerContent={
          hasHiddenColumns ? (
            <HiddenColumnsIndicator
              onCustomize={() => setIsCustomizeColumnsOpen((prev) => !prev)}
              buttonRef={customizeButtonRef}
            />
          ) : undefined
        }
      />

      <AlertDialog
        open={pendingDeleteIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteIds([]);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('simulations.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('simulations.bulk_delete_confirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleConfirmDelete()}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingRunAll}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRunAll(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('simulations.run_all_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('simulations.run_all_confirm', { count: simulations.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setPendingRunAll(false);
                onRunAll();
              }}
            >
              {t('simulations.run_all')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingEditId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingEditId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ai_agents.unsaved_changes.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('ai_agents.unsaved_changes.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = pendingEditId;
                setPendingEditId(null);
                if (id) {
                  openEditPanel(id);
                }
              }}
            >
              {t('common.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DataTable.Root>
  );
};
